import { memo, useMemo, type ReactNode } from "react";
import { EmptyState } from "@/shared/ui/emptyState/public";
import type { CompareRun } from "./evalCompareModel";
import { compareScenarioRows, SCENARIO_ROW_SEPARATOR } from "./evalCompareDiffModel";
import { CompareTable, compareRunColumns, diffTableRow } from "./CompareTable";
import tableStyles from "./CompareTable.module.css";

function splitScenarioRowKey(key: string): { group: string; field: string } {
  const separator = key.indexOf(SCENARIO_ROW_SEPARATOR);
  if (separator < 0) return { group: key, field: "" };
  return {
    group: key.slice(0, separator),
    field: key.slice(separator + SCENARIO_ROW_SEPARATOR.length),
  };
}

// memo 를 쓰는 이유는 PolicyCompareTable 과 같다. 시나리오 파라미터 행 × 수백 run 열인 이 표가
// 가장 크다.
export const ScenarioCompareTable = memo(function ScenarioCompareTable({
  items,
  onOpenRun,
  showRunSublabels = false,
}: {
  items: readonly CompareRun[];
  onOpenRun: (runId: string) => void;
  // 같은 정책의 run 열이 나란히 있는 정책 비교에서 켠다. seed 부제가 열을 구분한다.
  showRunSublabels?: boolean;
}): ReactNode {
  const rows = useMemo(() => compareScenarioRows(items), [items]);
  const columns = useMemo(
    () => compareRunColumns(items, onOpenRun, { showSublabel: showRunSublabels }),
    [items, onOpenRun, showRunSublabels],
  );
  if (rows.length === 0) {
    return <EmptyState size="sm">표시할 Scenario 정보가 없습니다.</EmptyState>;
  }
  const hasDiffs = rows.some((row) => row.differs);
  return (
    <CompareTable
      groupHeader="Scenario 값"
      leafHeader="항목"
      columns={columns}
      rows={rows.map((row) => {
        const { group, field } = splitScenarioRowKey(row.key);
        return diffTableRow(row, group, field);
      })}
      footer={
        hasDiffs ? (
          <p className={tableStyles.footnote}>실행 결과를 비교해 값이 다른 항목을 강조했습니다.</p>
        ) : null
      }
    />
  );
});
